import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";

/**
 * AddManualRowModal — books an asset addition the client's books never
 * captured as an FA-tagged Tally voucher (self-constructed asset, missed
 * capex entry, etc). Created row has no fa_ledger_id/voucher_id, but flows
 * straight into depreciation compute like any other addition once saved.
 */
export function AddManualRowModal({ blocks, defaultBlock, onSave, onClose }) {
  const [form, setForm] = useState({
    block_label: defaultBlock || "",
    description: "",
    party_name: "",
    invoice_no: "",
    invoice_date: "",
    put_to_use_date: "",
    invoice_cost: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSave = form.block_label && form.description.trim();

  const save = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave({ ...form, invoice_cost: parseFloat(form.invoice_cost) || 0 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
         data-testid="fa-add-manual-row-modal">
      <div className="bg-white border border-[#E5E5E0] w-full max-w-lg">
        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-[#EDEDE7]">
          <div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-600">Additions Register</div>
            <div className="font-heading text-base mt-0.5">Add New Row (Manual Entry)</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 p-1" data-testid="fa-add-manual-row-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">IT Block *</label>
            <select
              value={form.block_label}
              onChange={set("block_label")}
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-add-manual-row-block"
            >
              <option value="">— Select block —</option>
              {(blocks || []).map((b) => (
                <option key={b.block_label} value={b.block_label}>{b.block_label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Description of Asset *</label>
            <input
              value={form.description}
              onChange={set("description")}
              placeholder="e.g. Self-constructed Generator Set"
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
              data-testid="fa-add-manual-row-description"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Party Name</label>
              <input
                value={form.party_name}
                onChange={set("party_name")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-row-party"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Invoice No</label>
              <input
                value={form.invoice_no}
                onChange={set("invoice_no")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-row-invoice-no"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Invoice Date</label>
              <input
                type="date"
                value={form.invoice_date}
                onChange={set("invoice_date")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-row-invoice-date"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Put-to-Use Date</label>
              <input
                type="date"
                value={form.put_to_use_date}
                onChange={set("put_to_use_date")}
                className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700"
                data-testid="fa-add-manual-row-ptu-date"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-slate-600">Invoice Cost (₹)</label>
            <input
              type="text" inputMode="decimal"
              value={form.invoice_cost}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^-?\d*\.?\d*$/.test(v)) setForm((f) => ({ ...f, invoice_cost: v }));
              }}
              placeholder="Leave blank if amount isn't known yet — defaults to ₹0.00"
              className="w-full mt-1 px-2 py-1.5 text-[12.5px] border border-[#D4D4D0] focus:outline-none focus:border-slate-700 text-right font-mono"
              data-testid="fa-add-manual-row-invoice-cost"
            />
          </div>
          <div className="text-[10.5px] text-slate-500">
            Optional — enter it now, or leave blank and fill it in later directly on the row.
            Other Exp / ITC Reversed / Interest Cap / Forex / Discounts can also be filled in after saving.
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#EDEDE7] bg-[#FBFBF8]">
          <button onClick={onClose} className="px-3 py-1.5 text-[12.5px] border border-slate-300 hover:bg-slate-100">Cancel</button>
          <button
            data-testid="fa-add-manual-row-save"
            onClick={save}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {saving && <Loader2 size={12} className="animate-spin"/>} Add Row
          </button>
        </div>
      </div>
    </div>
  );
}
